import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Download, CheckCircle2, Loader2, ArrowRight, Lock } from 'lucide-react';
import { DRIVE_FOLDER_URL } from '../data/books';
import './SubscribeForm.css';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xqerzepz';

const INTEREST_OPTIONS = [
  'Building wealth with AI',
  'Starting an AI business',
  'Using AI in ministry',
  'Career / future of work',
  'Other',
];

const validators = {
  name: (v) => (v.trim().length >= 2 ? '' : 'Please enter your full name'),
  email: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) ? '' : 'Enter a valid email address'),
  phone: (v) => (v.trim().length >= 7 ? '' : 'Enter a valid phone number'),
  country: (v) => (v.trim().length >= 2 ? '' : 'Please enter your country'),
  interest: (v) => (v ? '' : 'Select one option'),
};

const SubscribeForm = ({ book, totalBooks = 5 }) => {
  const [values, setValues] = useState({
    name: '',
    email: '',
    phone: '',
    country: '',
    interest: '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | succeeded | error
  const [formError, setFormError] = useState('');

  const setField = (field) => (e) => {
    const value = e.target.value;
    setValues((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: validators[field](value) }));
    }
  };

  const handleBlur = (field) => () => {
    setErrors((prev) => ({ ...prev, [field]: validators[field](values[field]) }));
  };

  const validateAll = () => {
    const next = {};
    Object.keys(validators).forEach((f) => {
      const msg = validators[f](values[f]);
      if (msg) next[f] = msg;
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');
    if (!validateAll()) {
      const first = document.querySelector('.form-field.has-error input, .form-field.has-error select');
      first?.focus();
      return;
    }
    setStatus('submitting');

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          country: values.country.trim(),
          interest: values.interest || 'Not specified',
          book: book.title,
          _subject: `New download request: ${book.title}`,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setFormError(
          data?.errors?.map((err) => err.message).join(' ') ||
            data?.error ||
            'Something went wrong. Please try again.'
        );
        setStatus('error');
        return;
      }

      setStatus('succeeded');
      window.open(DRIVE_FOLDER_URL, '_blank', 'noopener,noreferrer');
    } catch {
      setFormError('Network error. Please check your connection and try again.');
      setStatus('error');
    }
  };

  if (status === 'succeeded') {
    return (
      <motion.div
        className="subscribe-success"
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="success-icon">
          <CheckCircle2 size={40} strokeWidth={1.75} />
        </div>
        <h2>You’re in{values.name ? `, ${values.name.split(' ')[0]}` : ''}!</h2>
        <p>
          Your free AI library just opened in a new tab. If it didn’t, use the button below —
          all {totalBooks} books are waiting for you.
        </p>
        <a className="btn btn-primary btn-block" href={DRIVE_FOLDER_URL} target="_blank" rel="noopener noreferrer">
          <Download size={19} />
          Open my free library
        </a>
        <p className="success-hint">Tip: bookmark the folder so you can return to your books anytime.</p>
      </motion.div>
    );
  }

  const fields = [
    { name: 'name', label: 'Full name', type: 'text', placeholder: 'e.g. Ada Okafor', autoComplete: 'name', inputMode: 'text' },
    { name: 'email', label: 'Email address', type: 'email', placeholder: 'you@example.com', autoComplete: 'email', inputMode: 'email' },
    { name: 'phone', label: 'Phone number', type: 'tel', placeholder: '+234 800 000 0000', autoComplete: 'tel', inputMode: 'tel' },
    { name: 'country', label: 'Country', type: 'text', placeholder: 'e.g. Nigeria', autoComplete: 'country-name', inputMode: 'text' },
  ];

  return (
    <form className="subscribe-form" onSubmit={handleSubmit} noValidate>
      <div className="form-head">
        <span className="form-badge">Free instant access</span>
        <h2>Get your free AI library</h2>
        <p>
          Enter your details to unlock <strong>{book.title}</strong> plus all {totalBooks} books —
          it takes under 30 seconds.
        </p>
      </div>

      {fields.map((f) => (
        <label className={`form-field ${errors[f.name] ? 'has-error' : ''}`} htmlFor={`sf-${f.name}`} key={f.name}>
          <span className="field-label">
            {f.label} <i aria-hidden="true">*</i>
          </span>
          <input
            id={`sf-${f.name}`}
            type={f.type}
            name={f.name}
            value={values[f.name]}
            onChange={setField(f.name)}
            onBlur={handleBlur(f.name)}
            placeholder={f.placeholder}
            autoComplete={f.autoComplete}
            inputMode={f.inputMode}
            disabled={status === 'submitting'}
            aria-invalid={!!errors[f.name]}
          />
          {errors[f.name] && (
            <span className="field-error" role="alert">
              {errors[f.name]}
            </span>
          )}
        </label>
      ))}

      <label className={`form-field ${errors.interest ? 'has-error' : ''}`} htmlFor="sf-interest">
        <span className="field-label">
          What are you most interested in? <i aria-hidden="true">*</i>
        </span>
        <select
          id="sf-interest"
          name="interest"
          value={values.interest}
          onChange={setField('interest')}
          onBlur={handleBlur('interest')}
          disabled={status === 'submitting'}
          aria-invalid={!!errors.interest}
        >
          <option value="" disabled>
            Select an option
          </option>
          {INTEREST_OPTIONS.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        {errors.interest && (
          <span className="field-error" role="alert">
            {errors.interest}
          </span>
        )}
      </label>

      {status === 'error' && formError && (
        <p className="form-error" role="alert">
          {formError}
        </p>
      )}

      <motion.button
        type="submit"
        className="btn btn-primary btn-block btn-lg submit-btn"
        disabled={status === 'submitting'}
        whileHover={status === 'submitting' ? undefined : { scale: 1.015 }}
        whileTap={status === 'submitting' ? undefined : { scale: 0.985 }}
      >
        {status === 'submitting' ? (
          <>
            <Loader2 size={20} className="spin" />
            Sending your books...
          </>
        ) : (
          <>
            Send me the free books
            <ArrowRight size={19} />
          </>
        )}
      </motion.button>

      <p className="form-privacy">
        <Lock size={13} />
        Your details are safe. No spam — unsubscribe anytime.
      </p>
    </form>
  );
};

export default SubscribeForm;
